import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { HeaderService } from '@app/shared/services/header.service';
import { UserService } from '@app/shared/services/user/user.service';
import { NavigationService } from '@app/shared/services/navigation/navigation.service';
import { combineLatest, concatMap, Observable, Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Building } from '@app/shared/models/building.interface';
import { TagChangeEvent, TagsComponent } from '@components/tags/tags.component';
import { SensorNodeService } from '@services/sensor-node.service';
import { FloorplanTag, Tag } from '@app/shared/models/tag.interface';
import { filter, first, map, takeUntil } from 'rxjs/operators';
import { PanelToggleComponent } from '@components/panel-toggle/panel-toggle.component';
import { MetricSelectorComponent } from '@app/analytics/metric-selector/metric-selector.component';
import { HeatmapLegendComponent } from '@components/heatmap-legend/heatmap-legend.component';
import { NgClass } from '@angular/common';
import { AnalyticsMetricsService } from '@services/analytics-metrics.service';
import { FloorplanService } from '@services/floorplan.service';
import { DataType } from '@app/shared/models/sensor-node-data-type';
import { SelectableNode } from '@app/shared/models/sensor-node';
import { FloorplanComponent } from '@components/floorplan/floorplan.component';
import { Resolution } from '@app/analytics/metric-widget/data-objects/resolution';
import { Dates } from '@app/analytics/metric-widget/query/outline/Dates';
import { IQueryContext } from '@app/analytics/metric-widget/query/context';
import { TimeUtils } from '@app/shared/utils/time.utils';
import { BuildingAuthorityType } from '@app/shared/models/building-authority-type';
import { SecurityService } from '@services/security.service';
import { TimeNavigatorService } from '@app/shared/time/time-navigator.service';
import { TagService } from '@services/tag.service';

@Component({
  selector: 'app-analytics',
  templateUrl: './analytics.component.html',
  imports: [
    PanelToggleComponent,
    MetricSelectorComponent,
    HeatmapLegendComponent,
    TagsComponent,
    NgClass,
    FloorplanComponent
  ],
  styleUrls: ['./analytics.component.scss']
})
export class AnalyticsComponent implements OnInit, OnDestroy, AfterViewInit {
  buildingId: number;
  floorId: number;
  year: number;
  month: number;
  day: number;
  hour: number;
  scale: string;
  building: Building;
  tags$: Observable<FloorplanTag[]>;

  isLeftColHidden = false;
  isRightColHidden = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly header: HeaderService,
    private readonly userService: UserService,
    private readonly navigationService: NavigationService,
    private readonly nodeService: SensorNodeService,
    private readonly route: ActivatedRoute,
    private readonly metricsService: AnalyticsMetricsService,
    private readonly floorplanService: FloorplanService,
    private readonly securityService: SecurityService,
    private readonly timeNavigatorService: TimeNavigatorService,
    private readonly tagService: TagService
  ) {}

  ngOnInit(): void {
    this.header.showSiteMenu();
    this.header.showBuildingsMenu();
    this.header.showFloorsMenu();
    this.header.showUserMenu();
    this.header.showSessionMenu();
    this.route.params
      .pipe(
        takeUntil(this.destroy$),
        concatMap((params) => {
          const { buildingId, floorId } = params;
          this.floorId = floorId;
          this.buildingId = buildingId;
          this.metricsService.updateContextBuilding(buildingId, floorId);
          this.addNodeChangeSubscription(buildingId);
          this.subscribeToNodeSelection();
          return this.userService.getBuilding(buildingId);
        })
      )
      .subscribe((building) => {
        this.building = building;
        this.navigationService.initNavigation(window.location.href, building);
        this.applyQueryParams();

        this.metricsService.currentContext$
          .pipe(
            filter((context: IQueryContext) => context != null),
            first()
          )
          .subscribe((_) => this.metricsService.updateQueryParams());

        this.securityService
          .isAuthorizedForBuilding(BuildingAuthorityType.HIGH_RESOLUTION_ANALYTICS, this.buildingId)
          .subscribe((result) =>
            result
              ? this.timeNavigatorService.addHighResolutionScale()
              : this.timeNavigatorService.removeHighResolutionScale()
          );
      });
  }

  applyQueryParams(): void {
    this.route.queryParams.pipe(first()).subscribe((params) => {
      const { year, month, day, hour, minute, scale, t, type } = params;
      const resolution = scale != null ? Resolution.fromString(scale.toUpperCase()) : Resolution.DAILY;
      const currentDate = TimeUtils.convertTimezone(new Date(), this.building.timeZone);
      const tagIds = Array.isArray(t) ? t.map(Number) : t ? [Number(t)] : [];
      const validatedType = type ? type.toUpperCase() : 'presence';

      let dataType: DataType;
      switch (validatedType.toUpperCase()) {
        case 'DIM_LEVEL':
          dataType = DataType.DIM_LEVEL;
          break;
        case 'ENERGY_CONSUMPTION':
          dataType = DataType.ENERGY_CONSUMPTION;
          break;
        default:
          dataType = DataType.PRESENCE;
      }

      let dates: Dates;
      if ([year, month, day, hour, minute, scale].every((value) => value == null)) {
        dates = new Dates(
          [currentDate.getFullYear()],
          [currentDate.getMonth() + 1],
          [currentDate.getDate()],
          [currentDate.getHours()]
        );
      } else {
        dates = new Dates(
          this.toNumberArray(year),
          this.toNumberArray(month),
          this.toNumberArray(day),
          this.toNumberArray(hour),
          this.toNumberArray(minute)
        );
      }
      this.metricsService.updateContextFromUrl(dates, resolution, tagIds, dataType);
    });
  }

  private toNumberArray(input: string | string[] | null | undefined): number[] {
    if (input == null) return []; // Handle null or undefined
    const strArray = Array.isArray(input) ? input : [input];
    return strArray.map(Number).filter((num) => !isNaN(num));
  }

  ngAfterViewInit(): void {
    this.floorplanService.enableAllNodesMode = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.floorplanService.heatmapMode = false;
    this.metricsService.updateContextDataType(DataType.PRESENCE);
    this.metricsService.tagContext = [];
  }

  toggleLeft(val: boolean): void {
    this.isLeftColHidden = val;
  }

  toggleRight(val: boolean): void {
    this.isRightColHidden = val;
  }

  get floorPanelClass(): string {
    if (this.isLeftColHidden && this.isRightColHidden) {
      return 'col-span-12';
    } else if (this.isLeftColHidden) {
      return 'col-span-10';
    } else if (this.isRightColHidden) {
      return 'col-span-10';
    } else {
      return 'col-span-8';
    }
  }

  addNodeChangeSubscription(buildingId: number): void {
    this.tags$ = combineLatest([
      this.tagService.getTagsForBuildingId(buildingId, false),
      this.nodeService.getCurrentFloorNodes$()
    ]).pipe(
      map(([tags, nodes]) => {
        const tagMap = new Map<number, Tag>();
        nodes?.forEach((node) => {
          if (node.tags) {
            node.tags.forEach((nodeTag) => {
              tagMap[nodeTag.id] = nodeTag;
            });
          }
        });

        const tagList = [];
        tags.forEach(tag => {
          if (tagMap[tag.id]) {
            tag.isActive = this.metricsService.currentContext.tagIds.includes(tag.id);
            tagList.push(tag);
          }
        });
        return tagList;
      })
    );
  }

  tagToggle(event: TagChangeEvent): void {
    const tag = event.tag;
    tag.isActive = event.isActive;
    const currentTagContext = JSON.parse(JSON.stringify(this.metricsService.currentContext.tagIds));
    if (event.isActive) {
      if (!currentTagContext.includes(tag.id)) {
        this.metricsService.tagContext = [...currentTagContext, tag.id];
      }
    } else {
      currentTagContext.splice(currentTagContext.indexOf(tag.id), 1);
      this.metricsService.tagContext = currentTagContext;
    }
    this.metricsService.updateQueryParams();
  }

  handleNodeClick(node: SelectableNode): void {
    if (this.floorplanService.isCumulativeModeActive) {
      this.nodeService.toggleEntitySelection(node, this.floorplanService.isCumulativeModeActive);
    } else {
      this.nodeService.selectEntitiesInArea([node], this.floorplanService.isCumulativeModeActive);
    }
    this.nodeService.updateQueryParams();
    this.nodeService.createNewNodesReference();
  }

  subscribeToNodeSelection(): void {
    this.nodeService
      .getCurrentFloorSelectedEntities$()
      .pipe(takeUntil(this.destroy$))
      .subscribe((selectedNodes) => {
        this.metricsService.updateNodeIs(selectedNodes.map((node) => node.id));
      });
  }
}
